import { useEffect, useMemo, useState } from 'react';
import { CartesianGrid, Line, LineChart, XAxis, YAxis } from 'recharts';
import type { RouteDefinition } from '@/routes';
import { applyRouteMeta } from '@/lib/meta';
import { AppLayout } from '@/layout/AppLayout';
import { AssumptionPanel } from '@/components/calculator/AssumptionPanel';
import { CalculatorSeoArticle } from '@/components/calculator/CalculatorSeoArticle';
import { ChartCard } from '@/components/calculator/ChartCard';
import { ContributionTimingControls } from '@/components/calculator/ContributionTimingControls';
import { FieldExplainerGuide } from '@/components/calculator/FieldExplainerGuide';
import { NumberField } from '@/components/calculator/NumberField';
import { ResultSummaryCard } from '@/components/calculator/ResultSummaryCard';
import { ShareExportRow } from '@/components/calculator/ShareExportRow';
import { RouteNextSteps } from '@/components/content/RouteNextSteps';
import { ChartContainer, ChartLegendPills, ChartTooltip, ChartTooltipContent, type ChartConfig } from '@/components/ui/chart';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { useToast } from '@/components/ui/toast';
import { getAdvancedFireFieldExplainers } from '@/content/field-explainers/advanced-fire';
import type { FieldExplainerContent } from '@/content/field-explainers/types';
import { calculateAdvancedFire, type AdvancedInput } from '@/engines/legacy-engines';
import {
  copyToClipboard,
  createContractShareUrl,
  exportPdfReport,
  exportRowsToCsv,
  formatCurrency,
  parseContractState
} from './shared';

type AdvancedState = {
  currentAge: number;
  retirementAge: number;
  lifeExpectancy: number;
  preRetirementGrowth: number;
  postRetirementGrowth: number;
  taxRate: number;
  withdrawalRate: number;
  inflationRate: number;
  contributionFrequency: 'monthly' | 'yearly';
  initial401k: number;
  initialRoth: number;
  initialTaxable: number;
  contribution401k: number;
  contributionRoth: number;
  contributionTaxable: number;
  contributionIncrease: number;
  penaltyFreeAge: number;
  bracketCapGross: number;
  bracketCapGrowth: number;
};

const defaults: AdvancedState = {
  currentAge: 35,
  retirementAge: 55,
  lifeExpectancy: 90,
  preRetirementGrowth: 7,
  postRetirementGrowth: 6,
  taxRate: 22,
  withdrawalRate: 4,
  inflationRate: 2.5,
  contributionFrequency: 'monthly',
  initial401k: 140000,
  initialRoth: 42000,
  initialTaxable: 32000,
  contribution401k: 1200,
  contributionRoth: 600,
  contributionTaxable: 800,
  contributionIncrease: 1.5,
  penaltyFreeAge: 59,
  bracketCapGross: 201775,
  bracketCapGrowth: 2.5
};

const chartConfig = {
  account401k: {
    label: '401(k)',
    color: 'var(--chart-1)'
  },
  accountRoth: {
    label: 'Roth IRA',
    color: 'var(--chart-2)'
  },
  accountTaxable: {
    label: 'Taxable',
    color: 'var(--chart-3)'
  },
  total: {
    label: 'Total',
    color: 'var(--chart-4)'
  }
} satisfies ChartConfig;

const balanceSeriesKeys = ['account401k', 'accountRoth', 'accountTaxable', 'total'] as const;

type Props = {
  route: RouteDefinition;
};

function toFiniteNumber(value: number, fallback: number): number {
  return Number.isFinite(value) ? value : fallback;
}

function formatCompactCurrency(value: number): string {
  if (!Number.isFinite(value)) {
    return '$0';
  }

  if (Math.abs(value) < 1000) {
    return `$${Math.round(value)}`;
  }

  return new Intl.NumberFormat('en-US', {
    notation: 'compact',
    style: 'currency',
    currency: 'USD',
    maximumFractionDigits: 1
  }).format(value);
}

function toEngineInput(state: AdvancedState): AdvancedInput {
  const currentAge = Math.max(18, Math.round(toFiniteNumber(state.currentAge, defaults.currentAge)));
  const retirementAge = Math.max(currentAge + 1, Math.round(toFiniteNumber(state.retirementAge, defaults.retirementAge)));
  const lifeExpectancy = Math.max(retirementAge + 1, Math.round(toFiniteNumber(state.lifeExpectancy, defaults.lifeExpectancy)));

  const preRetirementGrowth = Math.max(0, toFiniteNumber(state.preRetirementGrowth, defaults.preRetirementGrowth));
  const postRetirementGrowth = Math.max(0, toFiniteNumber(state.postRetirementGrowth, defaults.postRetirementGrowth));
  const taxRate = Math.max(0, toFiniteNumber(state.taxRate, defaults.taxRate));
  const withdrawalRate = Math.max(0, toFiniteNumber(state.withdrawalRate, defaults.withdrawalRate));
  const inflationRate = Math.max(0, toFiniteNumber(state.inflationRate, defaults.inflationRate));

  const initial401k = Math.max(0, toFiniteNumber(state.initial401k, defaults.initial401k));
  const initialRoth = Math.max(0, toFiniteNumber(state.initialRoth, defaults.initialRoth));
  const initialTaxable = Math.max(0, toFiniteNumber(state.initialTaxable, defaults.initialTaxable));
  const contribution401k = Math.max(0, toFiniteNumber(state.contribution401k, defaults.contribution401k));
  const contributionRoth = Math.max(0, toFiniteNumber(state.contributionRoth, defaults.contributionRoth));
  const contributionTaxable = Math.max(0, toFiniteNumber(state.contributionTaxable, defaults.contributionTaxable));
  const contributionIncrease = Math.max(0, toFiniteNumber(state.contributionIncrease, defaults.contributionIncrease));

  const penaltyFreeAge = Math.max(retirementAge, Math.round(toFiniteNumber(state.penaltyFreeAge, defaults.penaltyFreeAge)));
  const bracketCapGross = Math.max(0, toFiniteNumber(state.bracketCapGross, defaults.bracketCapGross));
  const bracketCapGrowth = Math.max(0, toFiniteNumber(state.bracketCapGrowth, defaults.bracketCapGrowth));

  return {
    currentAge,
    retirementAge,
    lifeExpectancy,
    preRetirementGrowth,
    postRetirementGrowth,
    taxRate,
    withdrawalRate,
    inflationRate,
    contributionFrequency: state.contributionFrequency,
    initial401k,
    initialRoth,
    initialTaxable,
    contribution401k,
    contributionRoth,
    contributionTaxable,
    contributionIncrease,
    penaltyFreeAge,
    bracketCapGross,
    bracketCapGrowth
  };
}

export function AdvancedFireCalculatorPage({ route }: Props) {
  const { toast } = useToast();
  const [state, setState] = useState<AdvancedState>(() => parseContractState('advancedFire', defaults));
  const [visibleBalanceKeys, setVisibleBalanceKeys] = useState<string[]>(() => [...balanceSeriesKeys]);

  useEffect(() => {
    applyRouteMeta(route);
  }, [route]);

  const normalizedInput = useMemo(() => toEngineInput(state), [state]);
  const result = useMemo(() => calculateAdvancedFire(normalizedInput), [normalizedInput]);
  const fallbackYear = {
    year: 0,
    age: normalizedInput.currentAge,
    balances: [0, 0, 0],
    total: 0,
    withdrawn: 0,
    withdrawalTarget: 0,
    withdrawalShortfall: 0,
    withdrawalFrom401k: 0,
    withdrawalFromRoth: 0,
    withdrawalFromTaxable: 0
  };
  const finalYear = result.years[result.years.length - 1] ?? fallbackYear;
  const retirementYear =
    result.years.find((year) => year.age === normalizedInput.retirementAge) ??
    result.years[0] ??
    fallbackYear;
  const lifetimeWithdrawn = result.years.reduce((sum, year) => sum + year.withdrawn, 0);
  const lifetimeShortfall = result.years.reduce((sum, year) => sum + year.withdrawalShortfall, 0);
  const firstRetirementYear = result.years.find((year) => year.withdrawalTarget > 0) ?? retirementYear;
  const depletionAge = result.years.find((year) => year.withdrawalShortfall > 0)?.age ?? null;

  const explainers = useMemo(() => {
    const entries = getAdvancedFireFieldExplainers({
      input: {
        currentAge: normalizedInput.currentAge,
        retirementAge: normalizedInput.retirementAge,
        lifeExpectancy: normalizedInput.lifeExpectancy,
        taxRate: normalizedInput.taxRate,
        preRetirementGrowth: normalizedInput.preRetirementGrowth,
        postRetirementGrowth: normalizedInput.postRetirementGrowth,
        withdrawalRate: normalizedInput.withdrawalRate,
        inflationRate: normalizedInput.inflationRate,
        contributionFrequency: normalizedInput.contributionFrequency,
        penaltyFreeAge: normalizedInput.penaltyFreeAge ?? defaults.penaltyFreeAge,
        bracketCapGross: normalizedInput.bracketCapGross ?? defaults.bracketCapGross,
        bracketCapGrowth: normalizedInput.bracketCapGrowth ?? defaults.bracketCapGrowth,
        initial401k: normalizedInput.initial401k,
        contribution401k: normalizedInput.contribution401k,
        initialRoth: normalizedInput.initialRoth,
        contributionRoth: normalizedInput.contributionRoth,
        initialTaxable: normalizedInput.initialTaxable,
        contributionTaxable: normalizedInput.contributionTaxable,
        contributionIncrease: normalizedInput.contributionIncrease
      },
      result: {
        retirementBalance: result.retirementBalance,
        baseWithdrawal: result.baseWithdrawal,
        retirementYearEndTotal: retirementYear.total,
        normalizedContribution401k: result.normalizedContribution401k,
        lifetimeWithdrawn,
        lifetimeShortfall,
        endingBalance: finalYear.total,
        endingAge: finalYear.age,
        endingBalances: [finalYear.balances[0] ?? 0, finalYear.balances[1] ?? 0, finalYear.balances[2] ?? 0],
        finalYearTarget: finalYear.withdrawalTarget,
        firstRetirementNet: firstRetirementYear.withdrawn,
        firstRetirementFrom401k: firstRetirementYear.withdrawalFrom401k,
        firstRetirementFromRoth: firstRetirementYear.withdrawalFromRoth,
        firstRetirementFromTaxable: firstRetirementYear.withdrawalFromTaxable,
        depletionAge
      }
    });
    const byId = new Map<string, FieldExplainerContent>(entries.map((entry) => [entry.id, entry]));
    return { entries, byId };
  }, [
    normalizedInput,
    result,
    retirementYear,
    finalYear,
    firstRetirementYear,
    lifetimeWithdrawn,
    lifetimeShortfall,
    depletionAge
  ]);

  const chartData = useMemo(
    () =>
      result.years.map((year) => ({
        age: year.age,
        account401k: year.balances[0] ?? 0,
        accountRoth: year.balances[1] ?? 0,
        accountTaxable: year.balances[2] ?? 0,
        total: year.total
      })),
    [result.years]
  );
  const chartDataIsRenderable = useMemo(
    () =>
      chartData.length > 0 &&
      chartData.every((point) =>
        Number.isFinite(point.account401k) &&
        Number.isFinite(point.accountRoth) &&
        Number.isFinite(point.accountTaxable) &&
        Number.isFinite(point.total)
      ),
    [chartData]
  );

  const shareState = {
    currentAge: state.currentAge,
    retirementAge: state.retirementAge,
    lifeExpectancy: state.lifeExpectancy,
    preRetirementGrowth: state.preRetirementGrowth,
    postRetirementGrowth: state.postRetirementGrowth,
    taxRate: state.taxRate,
    withdrawalRate: state.withdrawalRate,
    inflationRate: state.inflationRate,
    contributionFrequency: state.contributionFrequency,
    initial401k: state.initial401k,
    initialRoth: state.initialRoth,
    initialTaxable: state.initialTaxable,
    contribution401k: state.contribution401k,
    contributionRoth: state.contributionRoth,
    contributionTaxable: state.contributionTaxable,
    contributionIncrease: state.contributionIncrease,
    penaltyFreeAge: state.penaltyFreeAge,
    bracketCapGross: state.bracketCapGross,
    bracketCapGrowth: state.bracketCapGrowth
  };

  return (
    <AppLayout showRouteHowItWorks={false}>
      <section className="hero-shell rounded-2xl border border-border p-6 md:p-8">
        <h1 className="font-display text-4xl leading-tight md:text-5xl">FIRE Calculator with Multiple Accounts</h1>
        <p className="mt-3 max-w-3xl text-sm text-muted-foreground md:text-base">
          Model your 401(k), IRA, and taxable accounts separately — pre-retirement growth, tax-aware drawdown sequencing, and inflation-adjusted withdrawals with full lifecycle projections for each account type.
        </p>
      </section>

      <CalculatorSeoArticle canonicalPath={route.canonicalPath} placement="before" />

      <section className="grid gap-6 xl:grid-cols-[1.25fr_1fr]">
        <AssumptionPanel
          title="Lifecycle and drawdown assumptions"
          description="Set contribution timing, tax assumptions, and post-59 drawdown priorities with account-level controls."
        >
          <div className="grid gap-4 md:grid-cols-2">
            <NumberField id="advCurrentAge" label="Current age" value={state.currentAge} min={18} max={85} parseMode="integer" explainer={explainers.byId.get('currentAge')} onChange={(value) => setState((prev) => ({ ...prev, currentAge: value }))} />
            <NumberField id="advRetireAge" label="Retirement age" value={state.retirementAge} min={state.currentAge + 1} max={90} parseMode="integer" explainer={explainers.byId.get('retirementAge')} onChange={(value) => setState((prev) => ({ ...prev, retirementAge: value }))} />
            <NumberField id="advLifeExpectancy" label="Life expectancy" value={state.lifeExpectancy} min={state.retirementAge + 1} max={105} parseMode="integer" explainer={explainers.byId.get('lifeExpectancy')} onChange={(value) => setState((prev) => ({ ...prev, lifeExpectancy: value }))} />
            <NumberField id="advTaxRate" label="Tax rate" value={state.taxRate} min={0} max={45} step={0.1} suffix="%" explainer={explainers.byId.get('taxRate')} onChange={(value) => setState((prev) => ({ ...prev, taxRate: value }))} />
            <NumberField id="advPreGrowth" label="Pre-retirement growth" value={state.preRetirementGrowth} min={0} max={12} step={0.1} suffix="%" explainer={explainers.byId.get('preRetirementGrowth')} onChange={(value) => setState((prev) => ({ ...prev, preRetirementGrowth: value }))} />
            <NumberField id="advPostGrowth" label="Post-retirement growth" value={state.postRetirementGrowth} min={0} max={10} step={0.1} suffix="%" explainer={explainers.byId.get('postRetirementGrowth')} onChange={(value) => setState((prev) => ({ ...prev, postRetirementGrowth: value }))} />
            <NumberField id="advWithdrawalRate" label="Initial withdrawal rate" value={state.withdrawalRate} min={2} max={8} step={0.1} suffix="%" explainer={explainers.byId.get('withdrawalRate')} onChange={(value) => setState((prev) => ({ ...prev, withdrawalRate: value }))} />
            <NumberField id="advInflation" label="Inflation rate" value={state.inflationRate} min={0} max={7} step={0.1} suffix="%" explainer={explainers.byId.get('inflationRate')} onChange={(value) => setState((prev) => ({ ...prev, inflationRate: value }))} />
          </div>

          <ContributionTimingControls
            value={state.contributionFrequency}
            onValueChange={(value) => setState((prev) => ({ ...prev, contributionFrequency: value }))}
            inflationAdjusted={state.contributionIncrease > 0}
            onInflationAdjustedChange={(enabled) =>
              setState((prev) => ({
                ...prev,
                contributionIncrease: enabled ? Math.max(prev.contributionIncrease, 1) : 0
              }))
            }
          />

          <div className="grid gap-4 md:grid-cols-2">
            <NumberField id="advPenaltyFreeAge" label="Penalty-free 401(k) age" value={state.penaltyFreeAge} min={state.retirementAge} max={70} parseMode="integer" explainer={explainers.byId.get('penaltyFreeAge')} onChange={(value) => setState((prev) => ({ ...prev, penaltyFreeAge: value }))} />
            <NumberField
              id="advBracketCapGross"
              label="401(k) withdrawal cap"
              value={state.bracketCapGross}
              min={0}
              step={1000}
              parseMode="integer"
              prefix="$"
              helperText="Gross 401(k) withdrawal ceiling used to stay within the 24% tax bracket before switching to Roth/taxable."
              explainer={explainers.byId.get('bracketCapGross')}
              onChange={(value) => setState((prev) => ({ ...prev, bracketCapGross: value }))}
            />
            <NumberField
              id="advBracketCapGrowth"
              label="401(k) cap growth"
              value={state.bracketCapGrowth}
              min={0}
              max={8}
              step={0.1}
              suffix="%"
              helperText="Annual growth assumption for that 24% bracket withdrawal cap (default 2.5% per year)."
              explainer={explainers.byId.get('bracketCapGrowth')}
              onChange={(value) => setState((prev) => ({ ...prev, bracketCapGrowth: value }))}
            />
          </div>

          <div className="grid gap-4 md:grid-cols-2">
            <NumberField id="advInitial401k" label="Initial 401(k)" value={state.initial401k} min={0} step={1000} prefix="$" explainer={explainers.byId.get('initial401k')} onChange={(value) => setState((prev) => ({ ...prev, initial401k: value }))} />
            <NumberField id="advContribution401k" label="401(k) contribution" value={state.contribution401k} min={0} step={100} prefix="$" explainer={explainers.byId.get('contribution401k')} onChange={(value) => setState((prev) => ({ ...prev, contribution401k: value }))} />
            <NumberField id="advInitialRoth" label="Initial Roth" value={state.initialRoth} min={0} step={1000} prefix="$" explainer={explainers.byId.get('initialRoth')} onChange={(value) => setState((prev) => ({ ...prev, initialRoth: value }))} />
            <NumberField id="advContributionRoth" label="Roth contribution" value={state.contributionRoth} min={0} step={100} prefix="$" explainer={explainers.byId.get('contributionRoth')} onChange={(value) => setState((prev) => ({ ...prev, contributionRoth: value }))} />
            <NumberField id="advInitialTaxable" label="Initial taxable" value={state.initialTaxable} min={0} step={1000} prefix="$" explainer={explainers.byId.get('initialTaxable')} onChange={(value) => setState((prev) => ({ ...prev, initialTaxable: value }))} />
            <NumberField id="advContributionTaxable" label="Taxable contribution" value={state.contributionTaxable} min={0} step={100} prefix="$" explainer={explainers.byId.get('contributionTaxable')} onChange={(value) => setState((prev) => ({ ...prev, contributionTaxable: value }))} />
            <NumberField id="advContributionIncrease" label="Contribution increase" value={state.contributionIncrease} min={0} max={8} step={0.1} suffix="%" explainer={explainers.byId.get('contributionIncrease')} onChange={(value) => setState((prev) => ({ ...prev, contributionIncrease: value }))} />
          </div>
        </AssumptionPanel>

        <div className="space-y-4">
          <ResultSummaryCard label="Balance at retirement" value={formatCurrency(result.retirementBalance)} note={`entering age ${normalizedInput.retirementAge}, before the first withdrawal`} trend="up" explainer={explainers.byId.get('retirementBalance')} />
          <ResultSummaryCard label="Lifetime withdrawn (net)" value={formatCurrency(lifetimeWithdrawn)} note="sum of net-of-tax retirement income" trend="flat" explainer={explainers.byId.get('lifetimeWithdrawn')} />
          <ResultSummaryCard label="Lifetime shortfall" value={formatCurrency(lifetimeShortfall)} note="unmet target withdrawals (gross)" trend={lifetimeShortfall > 0 ? 'down' : 'flat'} explainer={explainers.byId.get('lifetimeShortfall')} />
          <ResultSummaryCard label="Ending balance" value={formatCurrency(finalYear.total)} note={`age ${finalYear.age}`} trend={finalYear.total > 0 ? 'up' : 'down'} explainer={explainers.byId.get('endingBalance')} />
          <ResultSummaryCard label="401(k) normalized contribution" value={formatCurrency(result.normalizedContribution401k)} note={`per ${state.contributionFrequency === 'monthly' ? 'month' : 'year'}`} trend="flat" explainer={explainers.byId.get('normalizedContribution401k')} />

          <ShareExportRow
            onCopyShare={async () => {
              const shareUrl = createContractShareUrl(route.canonicalPath, 'advancedFire', shareState);
              const ok = await copyToClipboard(shareUrl);
              toast({
                title: ok ? 'Share link copied' : 'Share link ready',
                description: ok ? shareUrl : `Copy manually: ${shareUrl}`,
                variant: ok ? 'success' : 'warning'
              });
            }}
            onExportCsv={() => {
              const headers = [
                'Age',
                '401k',
                'Roth',
                'Taxable',
                'Total',
                'Target (gross)',
                'Withdrawn (net)',
                '% of Total',
                'Shortfall',
                'From 401k',
                'From Roth',
                'From Taxable'
              ];
              const rows = result.years.map((year) => {
                const grossTaken = year.withdrawalTarget - year.withdrawalShortfall;
                const preWithdrawalTotal = year.total + grossTaken;
                const pctOfTotal =
                  preWithdrawalTotal > 0 && year.withdrawalTarget > 0
                    ? (year.withdrawalTarget / preWithdrawalTotal) * 100
                    : 0;
                return [
                  year.age,
                  Math.round(year.balances[0] ?? 0),
                  Math.round(year.balances[1] ?? 0),
                  Math.round(year.balances[2] ?? 0),
                  Math.round(year.total),
                  Math.round(year.withdrawalTarget),
                  Math.round(year.withdrawn),
                  pctOfTotal ? pctOfTotal.toFixed(2) : '0.00',
                  Math.round(year.withdrawalShortfall),
                  Math.round(year.withdrawalFrom401k),
                  Math.round(year.withdrawalFromRoth),
                  Math.round(year.withdrawalFromTaxable)
                ];
              });
              exportRowsToCsv(headers, rows, 'advanced-fire-projection.csv');
              toast({ title: 'CSV exported', description: 'advanced-fire-projection.csv downloaded.', variant: 'success' });
            }}
            onExportPdf={async () => {
              const ok = await exportPdfReport({
                title: 'Advanced FIRE Results',
                subtitle: `Ages ${state.currentAge} to ${state.lifeExpectancy}`,
                summaryLeft: [
                  `Retirement age: ${state.retirementAge}`,
                  `Penalty-free age: ${state.penaltyFreeAge}`,
                  `Tax rate: ${state.taxRate.toFixed(1)}%`,
                  `Withdrawal rate: ${state.withdrawalRate.toFixed(1)}%`,
                  `Inflation rate: ${state.inflationRate.toFixed(1)}%`
                ],
                summaryRight: [
                  `Cap gross: ${formatCurrency(state.bracketCapGross)}`,
                  `Cap growth: ${state.bracketCapGrowth.toFixed(1)}%`,
                  `Balance at retirement: ${formatCurrency(result.retirementBalance)}`,
                  `Ending balance: ${formatCurrency(finalYear.total)}`,
                  `Lifetime shortfall: ${formatCurrency(lifetimeShortfall)}`
                ],
                headers: ['Age', '401(k)', 'Roth IRA', 'Taxable', 'Total', 'Target (gross)', 'Withdrawn (net)', '% of Total', 'Shortfall', 'From 401(k)', 'From Roth', 'From Taxable'],
                rows: result.years.map((year) => {
                  const grossTaken = year.withdrawalTarget - year.withdrawalShortfall;
                  const preWithdrawalTotal = year.total + grossTaken;
                  const pctOfTotal =
                    preWithdrawalTotal > 0 && year.withdrawalTarget > 0
                      ? (year.withdrawalTarget / preWithdrawalTotal) * 100
                      : 0;
                  return [
                    year.age,
                    formatCurrency(year.balances[0] ?? 0),
                    formatCurrency(year.balances[1] ?? 0),
                    formatCurrency(year.balances[2] ?? 0),
                    formatCurrency(year.total),
                    formatCurrency(year.withdrawalTarget),
                    formatCurrency(year.withdrawn),
                    pctOfTotal ? `${pctOfTotal.toFixed(2)}%` : '—',
                    formatCurrency(year.withdrawalShortfall),
                    formatCurrency(year.withdrawalFrom401k),
                    formatCurrency(year.withdrawalFromRoth),
                    formatCurrency(year.withdrawalFromTaxable)
                  ];
                }),
                filename: 'advanced-fire-projection.pdf'
              });

              toast({
                title: ok ? 'PDF exported' : 'PDF export unavailable',
                description: ok ? 'advanced-fire-projection.pdf downloaded.' : 'PDF dependencies are not available right now.',
                variant: ok ? 'success' : 'warning'
              });
            }}
          />
        </div>
      </section>

      <section className="grid gap-6 xl:grid-cols-[minmax(0,1.2fr)_minmax(0,1fr)] [&>*]:min-w-0">
        <ChartCard
          title="Account balances over time"
          description="Full projection of each account type and total balance through accumulation and drawdown."
          annotation={
            <div className="flex flex-wrap items-center gap-x-4 gap-y-2">
              <span>
                Retirement age <span className="font-semibold text-foreground">{normalizedInput.retirementAge}</span>
              </span>
              <span>
                Lifetime withdrawn (net) <span className="font-semibold text-foreground">{formatCurrency(lifetimeWithdrawn)}</span>
              </span>
              <span>
                Shortfall <span className="font-semibold text-foreground">{formatCurrency(lifetimeShortfall)}</span>
              </span>
            </div>
          }
          legend={
            <ChartLegendPills
              config={chartConfig}
              keys={balanceSeriesKeys}
              activeKeys={visibleBalanceKeys}
              onActiveKeysChange={setVisibleBalanceKeys}
            />
          }
        >
          {chartDataIsRenderable ? (
            <ChartContainer config={chartConfig} family="multi">
              <LineChart
                accessibilityLayer
                data={chartData}
                margin={{
                  top: 18,
                  left: 12,
                  right: 12
                }}
              >
                <CartesianGrid vertical={false} />
                <XAxis dataKey="age" tickLine={false} axisLine={false} tickMargin={8} />
                <YAxis
                  tickLine={false}
                  axisLine={false}
                  tickMargin={8}
                  width={70}
                  tickFormatter={(value) => formatCompactCurrency(Number(value))}
                />
                <ChartTooltip
                  cursor={false}
                  content={<ChartTooltipContent indicator="line" formatter={(value) => formatCurrency(Number(value ?? 0))} />}
                />
                {visibleBalanceKeys.includes('account401k') ? <Line dataKey="account401k" type="natural" stroke="var(--color-account401k)" strokeWidth={2} dot={false} /> : null}
                {visibleBalanceKeys.includes('accountRoth') ? <Line dataKey="accountRoth" type="natural" stroke="var(--color-accountRoth)" strokeWidth={2} dot={false} /> : null}
                {visibleBalanceKeys.includes('accountTaxable') ? <Line dataKey="accountTaxable" type="natural" stroke="var(--color-accountTaxable)" strokeWidth={2} dot={false} /> : null}
                {visibleBalanceKeys.includes('total') ? <Line dataKey="total" type="natural" stroke="var(--color-total)" strokeWidth={3.2} dot={false} /> : null}
              </LineChart>
            </ChartContainer>
          ) : (
            <div className="flex h-72 items-center justify-center rounded-lg border border-dashed border-border px-4 text-center text-sm text-muted-foreground">
              Projection chart unavailable for the current assumptions. Adjust the inputs and recalculate.
            </div>
          )}
        </ChartCard>

        <ChartCard
          title="Projection table"
          description="Target is the gross withdrawal (e.g. 4% of the portfolio at retirement, inflation-adjusted). Withdrawn is the net amount received after taxes. % of Total shows the gross target as a share of that year's pre-withdrawal portfolio; values above 100% mean the target exceeded the available balance (see Shortfall)."
        >
          <Table containerClassName="max-h-[28rem]" stickyHeader compact>
            <TableHeader>
              <TableRow>
                <TableHead numeric>Age</TableHead>
                <TableHead numeric>Total</TableHead>
                <TableHead numeric>401(k)</TableHead>
                <TableHead numeric>Roth</TableHead>
                <TableHead numeric>Taxable</TableHead>
                <TableHead numeric>Target (gross)</TableHead>
                <TableHead numeric>Withdrawn (net)</TableHead>
                <TableHead numeric>% of Total</TableHead>
                <TableHead numeric>Shortfall</TableHead>
                <TableHead numeric>From 401(k)</TableHead>
                <TableHead numeric>From Roth</TableHead>
                <TableHead numeric>From Taxable</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {result.years.map((row) => {
                const grossTaken = row.withdrawalTarget - row.withdrawalShortfall;
                const preWithdrawalTotal = row.total + grossTaken;
                const pctOfTotal =
                  preWithdrawalTotal > 0 && row.withdrawalTarget > 0
                    ? (row.withdrawalTarget / preWithdrawalTotal) * 100
                    : 0;
                return (
                  <TableRow key={row.age}>
                    <TableCell numeric>{row.age}</TableCell>
                    <TableCell numeric>{formatCurrency(row.total)}</TableCell>
                    <TableCell numeric>{formatCurrency(row.balances[0] ?? 0)}</TableCell>
                    <TableCell numeric>{formatCurrency(row.balances[1] ?? 0)}</TableCell>
                    <TableCell numeric>{formatCurrency(row.balances[2] ?? 0)}</TableCell>
                    <TableCell numeric>{formatCurrency(row.withdrawalTarget)}</TableCell>
                    <TableCell numeric>{formatCurrency(row.withdrawn)}</TableCell>
                    <TableCell numeric>{pctOfTotal ? `${pctOfTotal.toFixed(2)}%` : '—'}</TableCell>
                    <TableCell numeric>{formatCurrency(row.withdrawalShortfall)}</TableCell>
                    <TableCell numeric>{formatCurrency(row.withdrawalFrom401k)}</TableCell>
                    <TableCell numeric>{formatCurrency(row.withdrawalFromRoth)}</TableCell>
                    <TableCell numeric>{formatCurrency(row.withdrawalFromTaxable)}</TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </ChartCard>
      </section>

      <section>
        <FieldExplainerGuide
          title="How each number is calculated"
          description="Every input and result on this page, explained with your numbers filled in. Tap the info icon next to any field for the same explanation in place."
          entries={explainers.entries}
        />
      </section>

      <RouteNextSteps canonicalPath={route.canonicalPath} />
      <CalculatorSeoArticle canonicalPath={route.canonicalPath} placement="after" />
    </AppLayout>
  );
}
